/**
 * Deterministic Executor for Warpway SDK
 *
 * Executes recorded actions with self-healing selector resolution
 * and handles both SPA and MPA navigation scenarios.
 */

import { RecordingSession, RecordedStep, SelectorPreference } from './types'

export interface ExecutorConfig {
  timeout?: number // Default timeout for operations (ms)
  retryAttempts?: number // Number of retry attempts for failed selectors
  logDrift?: boolean // Log when fallback selectors are used
  speedMultiplier?: number // Speed multiplier (0.5 = slower, 1.0 = normal, 2.0 = faster)
  showAnimations?: boolean // Show visual click/interaction animations
  siteId?: string // Site ID for API calls (e.g., intelligent option matching)
  apiUrl?: string // Base API URL
}

export interface ExecutionResult {
  success: boolean
  steps: StepResult[]
  durationMs: number
  driftDetected: boolean
  autoRecovered: boolean
  failureMessage?: string
  failureStep?: number
  failureType?: 'timeout' | 'selector_miss' | 'assertion_fail' | 'navigation_error' | 'unknown'
}

export interface StepResult {
  stepIndex: number
  type: string
  status: 'success' | 'failed' | 'skipped'
  durationMs: number
  selectorUsed?: SelectorPreference
  lowConfidence?: boolean
  error?: string
}

const STORAGE_KEY_RUN_ID = 'warpway:runId'
const STORAGE_KEY_STEP_INDEX = 'warpway:stepIndex'
const STORAGE_KEY_MANIFEST = 'warpway:manifest'

/**
 * Executor class that runs recorded actions deterministically
 */
export class Executor {
  private config: Required<ExecutorConfig>
  private results: StepResult[] = []
  private startTime: number = 0
  private driftCount: number = 0
  private autoRecoveredCount: number = 0

  constructor(config: ExecutorConfig = {}) {
    this.config = {
      timeout: config.timeout || 10000,
      retryAttempts: config.retryAttempts || 3,
      logDrift: config.logDrift !== false,
      speedMultiplier: config.speedMultiplier !== undefined ? config.speedMultiplier : 0.7, // Default to slower, more human-like
      showAnimations: config.showAnimations !== false, // Default to true
      siteId: config.siteId || '',
      apiUrl: config.apiUrl || '',
    }
  }

  /**
   * Execute a manifest (array of steps or object with startingUrl)
   */
  async execute(
    manifestOrSteps: RecordedStep[] | { startingUrl?: string; steps: RecordedStep[] },
    onStepStart?: (stepIndex: number, step: RecordedStep) => void
  ): Promise<ExecutionResult> {
    // Normalize input: support both legacy array format and new manifest object format
    let startingUrl: string | undefined
    let steps: RecordedStep[]

    if (Array.isArray(manifestOrSteps)) {
      // Legacy format: just an array of steps
      steps = manifestOrSteps
      startingUrl = undefined
    } else {
      // New format: manifest object with startingUrl and steps
      startingUrl = manifestOrSteps.startingUrl
      steps = manifestOrSteps.steps
    }

    console.log('[Warpway Executor] Starting execution', {
      steps: steps.length,
      startingUrl,
      currentUrl: window.location.href
    })

    this.startTime = Date.now()
    this.results = []
    this.driftCount = 0
    this.autoRecoveredCount = 0

    // Store manifest in sessionStorage FIRST (before navigation) for hard reload recovery
    try {
      const manifestData = { startingUrl, steps }
      sessionStorage.setItem(STORAGE_KEY_MANIFEST, JSON.stringify(manifestData))
      sessionStorage.setItem(STORAGE_KEY_STEP_INDEX, '0')
      sessionStorage.setItem('warpway:executionInProgress', 'true')

      console.log('[Warpway Executor] ✅ Saved execution state to sessionStorage:', {
        manifest: STORAGE_KEY_MANIFEST,
        stepIndex: '0',
        executionInProgress: 'true',
        stepsCount: steps.length,
        startingUrl
      })

      // Verify it was saved
      const saved = sessionStorage.getItem(STORAGE_KEY_MANIFEST)
      if (!saved) {
        console.error('[Warpway Executor] ❌ sessionStorage save verification FAILED!')
      }
    } catch (e) {
      console.error('[Warpway Executor] ❌ Failed to save state to sessionStorage:', e)
    }

    // Navigate to starting URL if needed (AFTER saving state)
    if (startingUrl && !this.urlsMatch(window.location.href, startingUrl)) {
      console.log('[Warpway Executor] Current page does not match starting URL, navigating...', {
        current: window.location.href,
        target: startingUrl
      })

      try {
        await this.navigateToStartingUrl(startingUrl)
        // Execution stops here - page reloads and setupAutoResume() will continue
      } catch (error) {
        const durationMs = Date.now() - this.startTime
        return {
          success: false,
          steps: [],
          durationMs,
          driftDetected: false,
          autoRecovered: false,
          failureMessage: `Failed to navigate to starting URL: ${startingUrl}`,
          failureStep: -1,
          failureType: 'navigation_error',
        }
      }
    } else if (startingUrl) {
      console.log('[Warpway Executor] Already on correct starting page')
    }

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]

      // Notify callback of step start
      if (onStepStart) {
        onStepStart(i, step)
      }

      // For steps that cause page reload (navigate, refresh), save the NEXT step index
      // so when we resume, we skip the navigation step and continue from next step
      const willCauseReload = step.type === 'navigate' || step.type === 'refresh'
      const resumeIndex = willCauseReload ? i + 1 : i

      // Update current step index for recovery
      try {
        sessionStorage.setItem(STORAGE_KEY_STEP_INDEX, String(resumeIndex))
      } catch (e) {
        // Ignore sessionStorage errors
      }

      const stepResult = await this.executeStep(step, i)
      this.results.push(stepResult)

      if (stepResult.status === 'failed') {
        const durationMs = Date.now() - this.startTime
        const failureType = this.categorizeFailure(stepResult)
        return {
          success: false,
          steps: this.results,
          durationMs,
          driftDetected: this.driftCount > 0,
          autoRecovered: this.autoRecoveredCount > 0,
          failureMessage: stepResult.error || 'Step execution failed',
          failureStep: i,
          failureType,
        }
      }
    }

    // Clear recovery state on success
    try {
      sessionStorage.removeItem(STORAGE_KEY_MANIFEST)
      sessionStorage.removeItem(STORAGE_KEY_STEP_INDEX)
      sessionStorage.removeItem('warpway:executionInProgress')
    } catch (e) {
      // Ignore
    }

    const durationMs = Date.now() - this.startTime
    return {
      success: true,
      steps: this.results,
      durationMs,
      driftDetected: this.driftCount > 0,
      autoRecovered: this.autoRecoveredCount > 0,
    }
  }

  /**
   * Resume execution after a hard reload
   */
  async resume(): Promise<ExecutionResult | null> {
    try {
      const manifestStr = sessionStorage.getItem(STORAGE_KEY_MANIFEST)
      const stepIndexStr = sessionStorage.getItem(STORAGE_KEY_STEP_INDEX)

      if (!manifestStr || !stepIndexStr) {
        return null // No resume state found
      }

      const manifest = JSON.parse(manifestStr)
      const startIndex = parseInt(stepIndexStr, 10)

      // Support both old (array) and new (object with startingUrl) formats
      let steps: RecordedStep[]
      if (Array.isArray(manifest)) {
        steps = manifest
      } else {
        steps = manifest.steps || []
      }

      console.log('[Warpway Executor] Resuming execution', {
        startIndex,
        totalSteps: steps.length
      })

      // Execute remaining steps (no need to navigate again, we're already on the right page)
      return await this.execute(steps.slice(startIndex))
    } catch (e) {
      console.error('[Warpway Executor] Failed to resume execution', e)
      return null
    }
  }

  /**
   * Categorize failure type based on error message and step result
   */
  private categorizeFailure(stepResult: StepResult): 'timeout' | 'selector_miss' | 'assertion_fail' | 'navigation_error' | 'unknown' {
    const error = stepResult.error?.toLowerCase() || ''

    // Check for timeout errors
    if (error.includes('timeout') || error.includes('timed out')) {
      return 'timeout'
    }

    // Check for selector miss errors
    if (error.includes('selector') || error.includes('element not found') || error.includes('could not find')) {
      return 'selector_miss'
    }

    // Check for assertion failures
    if (stepResult.type === 'assert:text' || error.includes('assertion') || error.includes('expected')) {
      return 'assertion_fail'
    }

    // Check for navigation errors
    if (stepResult.type === 'navigate' || error.includes('navigation') || error.includes('failed to load')) {
      return 'navigation_error'
    }

    return 'unknown'
  }

  /**
   * Execute a single step
   */
  private async executeStep(step: RecordedStep, stepIndex: number): Promise<StepResult> {
    const startTime = Date.now()
    const stepResult: StepResult = {
      stepIndex,
      type: step.type,
      status: 'success',
      durationMs: 0,
    }

    try {
      switch (step.type) {
        case 'waitFor':
          await this.waitFor(step, stepResult)
          break
        case 'click':
          await this.click(step, stepResult)
          break
        case 'fill':
          await this.fill(step, stepResult)
          break
        case 'navigate':
          await this.navigate(step, stepResult)
          break
        case 'refresh':
          await this.refresh(step, stepResult)
          break
        case 'assert:text':
          await this.assertText(step, stepResult)
          break
        default:
          console.warn('[Warpway Executor] Unknown step type:', step.type)
      }

      stepResult.durationMs = Date.now() - startTime
      return stepResult
    } catch (error) {
      stepResult.status = 'failed'
      stepResult.durationMs = Date.now() - startTime
      stepResult.error = error instanceof Error ? error.message : 'Unknown error'
      return stepResult
    }
  }

  /**
   * Wait for an element to be visible
   */
  private async waitFor(step: RecordedStep, result: StepResult): Promise<void> {
    const selectors = step.selectors || []
    if (selectors.length === 0) {
      throw new Error('No selectors provided for waitFor')
    }

    const element = await this.findElementWithFallback(selectors, result)
    if (!element) {
      throw new Error(`Element not found after ${this.config.timeout}ms`)
    }

    // Wait for visibility
    await this.waitForVisible(element, this.config.timeout)
  }

  /**
   * Click an element
   */
  private async click(step: RecordedStep, result: StepResult): Promise<void> {
    const selectors = step.selectors || []
    if (selectors.length === 0) {
      throw new Error('No selectors provided for click')
    }

    const element = await this.findElementWithFallback(selectors, result)
    if (!element) {
      throw new Error('Element not found for click')
    }

    await this.waitForVisible(element, this.config.timeout)

    // Scroll into view if needed
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    await this.sleep(100) // Brief pause for scroll

    // Show animation before click
    if (this.config.showAnimations) {
      this.showInteractionAnimation(element)
    }

    // Click the element
    (element as HTMLElement).click()

    // Brief pause after click to allow for event processing
    await this.sleep(50)
  }

  /**
   * Fill an input element
   */
  private async fill(step: RecordedStep, result: StepResult): Promise<void> {
    const selectors = step.selectors || []
    const value = step.value

    console.log('[Warpway Executor] Fill step:', {
      selectors: selectors.map((s: SelectorPreference) => s.selector),
      value,
      stepObject: step
    })

    if (selectors.length === 0) {
      throw new Error('No selectors provided for fill')
    }
    if (value === undefined || value === null) {
      throw new Error('No value provided for fill')
    }

    const element = await this.findElementWithFallback(selectors, result)
    if (!element) {
      throw new Error('Element not found for fill')
    }

    if (!(element instanceof HTMLInputElement) &&
        !(element instanceof HTMLTextAreaElement) &&
        !(element instanceof HTMLSelectElement)) {
      throw new Error('Element is not a fillable input')
    }

    await this.waitForVisible(element, this.config.timeout)
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    await this.sleep(100)

    // Show animation before filling
    if (this.config.showAnimations) {
      this.showInteractionAnimation(element)
    }

    // Handle select elements differently
    if (element instanceof HTMLSelectElement) {
      await this.fillSelect(element, value)
      return
    }

    // For input/textarea, use character-by-character typing to bypass React
    await this.typeCharacterByCharacter(element, value)
  }

  /**
   * Fill a select element with intelligent option matching
   */
  private async fillSelect(element: HTMLSelectElement, value: string, fieldLabel?: string): Promise<void> {
    const options = Array.from(element.options).map(o => ({ value: o.value, text: o.text }))

    console.log('[Warpway Executor] Filling select element:', {
      targetValue: value,
      availableOptions: options
    })

    element.focus()
    await this.sleep(50)

    // Get the native setter
    const nativeSelectValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLSelectElement.prototype,
      'value'
    )?.set

    // Try exact match on option value (fast path)
    const exactValueMatch = options.find(o => o.value === value)
    if (exactValueMatch) {
      console.log('[Warpway Executor] Found exact option value match')
      if (nativeSelectValueSetter) {
        nativeSelectValueSetter.call(element, value)
      } else {
        element.value = value
      }
      element.dispatchEvent(new Event('change', { bubbles: true }))
      element.blur()
      await this.sleep(50)
      return
    }

    // Try exact text match (case-insensitive)
    const exactTextMatch = options.find(o => o.text.toLowerCase() === value.toLowerCase())
    if (exactTextMatch) {
      console.log('[Warpway Executor] Found exact option text match')
      if (nativeSelectValueSetter) {
        nativeSelectValueSetter.call(element, exactTextMatch.value)
      } else {
        element.value = exactTextMatch.value
      }
      element.dispatchEvent(new Event('change', { bubbles: true }))
      element.blur()
      await this.sleep(50)
      return
    }

    // No exact match - try intelligent matching via API
    if (this.config.siteId && this.config.apiUrl) {
      console.log('[Warpway Executor] No exact match, trying intelligent option matching via API...')

      // Add simple pulsing outline while loading
      if (this.config.showAnimations) {
        element.classList.add('warpway-loading')
        this.injectLoadingStyles()
      }

      try {
        const matchResult = await this.matchOptionViaAPI(value, options, fieldLabel)

        // Remove loading state
        element.classList.remove('warpway-loading')

        if (matchResult.matched_value && matchResult.confidence >= 0.7) {
          console.log('[Warpway Executor] AI matched option:', {
            matched: matchResult.matched_text,
            confidence: matchResult.confidence,
            reasoning: matchResult.reasoning
          })

          if (nativeSelectValueSetter) {
            nativeSelectValueSetter.call(element, matchResult.matched_value)
          } else {
            element.value = matchResult.matched_value
          }
          element.dispatchEvent(new Event('change', { bubbles: true }))
          element.blur()
          await this.sleep(50)
          return
        } else {
          console.warn('[Warpway Executor] AI matching confidence too low or no match:', matchResult.confidence)
        }
      } catch (error) {
        // Remove loading state on error
        element.classList.remove('warpway-loading')
        console.error('[Warpway Executor] API matching failed:', error)
      }
    }

    // No match found - log available options for debugging
    console.warn('[Warpway Executor] No match found for select value:', value)
    console.warn('[Warpway Executor] Available options:', options)
    element.blur()
    await this.sleep(50)
  }

  /**
   * Call the backend API to intelligently match a dropdown option
   */
  private async matchOptionViaAPI(
    userInput: string,
    options: { value: string; text: string }[],
    fieldLabel?: string
  ): Promise<{
    matched_value: string | null
    matched_text: string | null
    confidence: number
    reasoning: string
  }> {
    const url = `${this.config.apiUrl}/v1/actions/match-option`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Warpway-Site-ID': this.config.siteId,
      },
      body: JSON.stringify({
        user_input: userInput,
        options: options,
        field_label: fieldLabel,
      }),
    })

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`)
    }

    return await response.json()
  }

  /**
   * Type value character-by-character to bypass React controlled inputs
   */
  private async typeCharacterByCharacter(
    element: HTMLInputElement | HTMLTextAreaElement,
    value: string
  ): Promise<void> {
    console.log('[Warpway Executor] Using character-by-character typing:', {
      currentValue: element.value,
      targetValue: value,
      elementType: element.constructor.name,
      inputType: element instanceof HTMLInputElement ? element.type : 'N/A'
    })

    // For email inputs, temporarily change type to text to avoid validation during typing
    let originalType: string | null = null
    if (element instanceof HTMLInputElement && element.type === 'email') {
      originalType = element.type
      element.type = 'text'
      console.log('[Warpway Executor] Temporarily changed email input to text')
    }

    element.focus()
    await this.sleep(50)

    // Get the native setter
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set
    const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype,
      'value'
    )?.set

    // Clear the field first
    if (element instanceof HTMLInputElement && nativeInputValueSetter) {
      nativeInputValueSetter.call(element, '')
    } else if (element instanceof HTMLTextAreaElement && nativeTextAreaValueSetter) {
      nativeTextAreaValueSetter.call(element, '')
    } else {
      element.value = ''
    }
    element.dispatchEvent(new Event('input', { bubbles: true }))
    await this.sleep(20)

    // Type each character one by one
    for (let i = 0; i < value.length; i++) {
      const currentValue = value.substring(0, i + 1)

      if (element instanceof HTMLInputElement && nativeInputValueSetter) {
        nativeInputValueSetter.call(element, currentValue)
      } else if (element instanceof HTMLTextAreaElement && nativeTextAreaValueSetter) {
        nativeTextAreaValueSetter.call(element, currentValue)
      } else {
        element.value = currentValue
      }

      // Dispatch input event after each character
      element.dispatchEvent(new Event('input', { bubbles: true }))

      // Small delay between characters (like real typing)
      await this.sleep(10)
    }

    // Restore original type if we changed it
    if (originalType && element instanceof HTMLInputElement) {
      element.type = originalType
      console.log('[Warpway Executor] Restored input type to:', originalType)
    }

    // Final change event
    await this.sleep(20)
    element.dispatchEvent(new Event('change', { bubbles: true }))

    console.log('[Warpway Executor] After typing:', {
      finalValue: element.value,
      matches: element.value === value
    })

    element.blur()
    await this.sleep(50)
  }

  /**
   * Navigate to a URL
   */
  private async navigate(step: RecordedStep, result: StepResult): Promise<void> {
    if (!step.url) {
      throw new Error('No URL provided for navigate')
    }

    // Check if this is SPA navigation or hard navigation
    const currentOrigin = window.location.origin
    const targetUrl = new URL(step.url, currentOrigin)

    if (targetUrl.origin === currentOrigin) {
      // Same origin - could be SPA navigation
      console.log('[Warpway Executor] SPA navigation to:', step.url)
      window.history.pushState({}, '', step.url)

      // Dispatch popstate event for SPA routers
      window.dispatchEvent(new PopStateEvent('popstate', { state: {} }))

      // Wait for page to update
      await this.sleep(500)
    } else {
      // Cross-origin - hard navigation
      console.log('[Warpway Executor] Hard navigation to:', step.url)
      window.location.href = step.url
      // Execution will stop here and resume after page load
    }
  }

  /**
   * Refresh the current page
   */
  private async refresh(step: RecordedStep, result: StepResult): Promise<void> {
    console.log('[Warpway Executor] Refreshing page')

    // Force a hard reload to ensure the page refreshes even if we're already on it
    window.location.reload()

    // Execution will stop here and resume after page load via setupAutoResume()
    // This promise never resolves because the page reloads
    return new Promise(() => {})
  }

  /**
   * Assert that text exists on the page
   */
  private async assertText(step: RecordedStep, result: StepResult): Promise<void> {
    const expectedText = step.value
    if (!expectedText) {
      throw new Error('No text provided for assert')
    }

    const timeout = this.config.timeout
    const startTime = Date.now()

    while (Date.now() - startTime < timeout) {
      const bodyText = document.body.textContent || ''
      if (bodyText.includes(expectedText)) {
        return // Assertion passed
      }
      await this.sleep(100)
    }

    throw new Error(`Text "${expectedText}" not found within ${timeout}ms`)
  }

  /**
   * Find element using selector preference stack with fallback
   * Implements self-healing by trying selectors in order of confidence
   */
  private async findElementWithFallback(
    selectors: SelectorPreference[],
    result: StepResult
  ): Promise<Element | null> {
    // Sort by confidence (highest first)
    const sortedSelectors = [...selectors].sort((a, b) => b.confidence - a.confidence)

    for (let i = 0; i < sortedSelectors.length; i++) {
      const selectorPref = sortedSelectors[i]

      try {
        const element = await this.findElement(selectorPref.selector, this.config.timeout)

        if (element) {
          result.selectorUsed = selectorPref

          // Check if we used a fallback selector (not the highest confidence)
          if (i > 0) {
            result.lowConfidence = true
            this.driftCount++
            this.autoRecoveredCount++

            if (this.config.logDrift) {
              console.warn('[Warpway Executor] Drift detected - used fallback selector', {
                expected: sortedSelectors[0],
                used: selectorPref,
              })
            }
          }

          return element
        }
      } catch (e) {
        // Try next selector
        continue
      }
    }

    return null // No selector found element
  }

  /**
   * Find element with timeout
   */
  private async findElement(selector: string, timeout: number): Promise<Element | null> {
    const startTime = Date.now()

    // Handle special selectors
    if (selector.includes(':has-text(')) {
      return await this.findByText(selector, timeout)
    }

    while (Date.now() - startTime < timeout) {
      try {
        const element = document.querySelector(selector)
        if (element) {
          return element
        }
      } catch (e) {
        // Invalid selector, try next
        return null
      }
      await this.sleep(100)
    }

    return null
  }

  /**
   * Find element by text content
   */
  private async findByText(selector: string, timeout: number): Promise<Element | null> {
    const match = selector.match(/^([a-z0-9]+):has-text\("([^"]+)"\)$/i)
    if (!match) {
      return null
    }

    const [, tagName, text] = match
    const startTime = Date.now()

    while (Date.now() - startTime < timeout) {
      const elements = document.querySelectorAll(tagName)
      for (const el of Array.from(elements)) {
        if (el.textContent?.trim() === text) {
          return el
        }
      }
      await this.sleep(100)
    }

    return null
  }

  /**
   * Wait for element to be visible
   */
  private async waitForVisible(element: Element, timeout: number): Promise<void> {
    const startTime = Date.now()

    while (Date.now() - startTime < timeout) {
      if (element instanceof HTMLElement) {
        const style = window.getComputedStyle(element)
        if (style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0') {
          return
        }
      }
      await this.sleep(100)
    }

    throw new Error('Element not visible within timeout')
  }

  /**
   * Sleep utility
   */
  private sleep(ms: number): Promise<void> {
    const adjustedMs = ms / this.config.speedMultiplier
    return new Promise(resolve => setTimeout(resolve, adjustedMs))
  }

  /**
   * Inject simple loading styles for pulsing outline
   */
  private injectLoadingStyles(): void {
    if (document.getElementById('warpway-loading-styles')) {
      return // Already injected
    }

    const style = document.createElement('style')
    style.id = 'warpway-loading-styles'
    style.textContent = `
      @keyframes warpway-pulse {
        0%, 100% {
          outline: 2px solid rgba(139, 92, 246, 0.3);
          outline-offset: 2px;
        }
        50% {
          outline: 2px solid rgba(139, 92, 246, 0.8);
          outline-offset: 2px;
        }
      }
      .warpway-loading {
        animation: warpway-pulse 1s ease-in-out infinite !important;
      }
    `
    document.head.appendChild(style)
  }

  /**
   * Compare two URLs to determine if they match
   * Handles both exact matches and SPA route patterns
   */
  private urlsMatch(currentUrl: string, targetUrl: string): boolean {
    try {
      const current = new URL(currentUrl)
      const target = new URL(targetUrl)

      // Different origins always require navigation
      if (current.origin !== target.origin) {
        return false
      }

      // Same origin - check if paths match (exact match)
      if (current.pathname === target.pathname) {
        return true
      }

      // For SPAs, we might be more lenient with query params and hashes
      // But for safety, require exact pathname match
      return false
    } catch (e) {
      // Invalid URL format, fall back to string comparison
      return currentUrl === targetUrl
    }
  }

  /**
   * Navigate to the starting URL before executing action
   * Handles both SPA and MPA hard navigation with resume support
   */
  private async navigateToStartingUrl(targetUrl: string): Promise<void> {
    const currentOrigin = window.location.origin
    const target = new URL(targetUrl, currentOrigin)

    if (target.origin === currentOrigin) {
      // Same origin - do hard navigation for MPAs
      // The page will reload and setupAutoResume() will continue execution
      console.log('[Warpway Executor] Navigating to starting URL (will reload page):', targetUrl)
      console.log('[Warpway Executor] Execution will auto-resume after page load')

      // Note: The manifest is already saved to sessionStorage in execute()
      // so after navigation, the page will reload and setupAutoResume() will pick it up

      // Do hard navigation (will stop current execution)
      window.location.href = targetUrl

      // Execution stops here - the code below won't run
      // After page loads, setupAutoResume() will detect state and call resume()

      // This promise never resolves because the page reloads
      return new Promise(() => {})
    } else {
      // Cross-origin - not supported
      console.log('[Warpway Executor] Cross-origin navigation to:', targetUrl)
      throw new Error('Cross-origin navigation not supported in executor. Action must start on same domain.')
    }
  }

  /**
   * Show visual animation on element interaction
   */
  private showInteractionAnimation(element: Element): void {
    if (!this.config.showAnimations) {
      return
    }

    const rect = element.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    // Create animation container
    const container = document.createElement('div')
    container.style.cssText = `
      position: fixed;
      left: ${centerX}px;
      top: ${centerY}px;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 999999;
      width: 80px;
      height: 80px;
    `

    // Outer ring
    const outerRing = document.createElement('div')
    outerRing.style.cssText = `
      position: absolute;
      inset: 0;
      border-radius: 50%;
      border: 2px solid #8b5cf6;
      opacity: 0.6;
      animation: warpway-ping 400ms ease-out;
    `

    // Inner glow
    const innerGlow = document.createElement('div')
    innerGlow.style.cssText = `
      position: absolute;
      inset: 8px;
      border-radius: 50%;
      background: rgba(139, 92, 246, 0.2);
      filter: blur(12px);
      animation: warpway-pulse 400ms ease-out;
    `

    // Center dot
    const centerDot = document.createElement('div')
    centerDot.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #8b5cf6;
      box-shadow: 0 0 12px rgba(139, 92, 246, 0.8);
    `

    // Sweep line
    const sweepLine = document.createElement('div')
    sweepLine.style.cssText = `
      position: absolute;
      inset: 0;
      border-radius: 50%;
      overflow: hidden;
      background: conic-gradient(from 0deg, transparent 0deg, #8b5cf6 45deg, transparent 90deg);
      animation: warpway-spin 400ms linear;
    `

    container.appendChild(outerRing)
    container.appendChild(innerGlow)
    container.appendChild(centerDot)
    container.appendChild(sweepLine)
    document.body.appendChild(container)

    // Inject CSS animations if not already present
    if (!document.getElementById('warpway-animations')) {
      const style = document.createElement('style')
      style.id = 'warpway-animations'
      style.textContent = `
        @keyframes warpway-ping {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes warpway-pulse {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.4; }
        }
        @keyframes warpway-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `
      document.head.appendChild(style)
    }

    // Remove after animation completes
    setTimeout(() => {
      container.remove()
    }, 400)
  }
}

/**
 * Detect if page loaded after a hard navigation during test execution
 * and automatically resume execution
 */
export function setupAutoResume(): void {
  if (typeof window === 'undefined') return

  const attemptResume = async () => {
    try {
      const hasResumeState = sessionStorage.getItem(STORAGE_KEY_MANIFEST)
      const executionInProgress = sessionStorage.getItem('warpway:executionInProgress')

      console.log('[Warpway Executor] Checking for resume state:', {
        hasResumeState: !!hasResumeState,
        executionInProgress: !!executionInProgress,
        readyState: document.readyState
      })

      if (hasResumeState && executionInProgress) {
        console.log('[Warpway Executor] Detected resume state after page navigation, auto-resuming execution...')

        // Show visual notification that execution is resuming
        showResumeNotification()

        const executor = new Executor()
        const result = await executor.resume()

        if (result) {
          console.log('[Warpway Executor] Resume complete', result)

          // Clear the execution flag
          sessionStorage.removeItem('warpway:executionInProgress')

          // Show result notification
          if (result.success) {
            showSuccessNotification()
          } else {
            showErrorNotification(result.failureMessage)
          }
        }
      } else if (hasResumeState || executionInProgress) {
        console.log('[Warpway Executor] Incomplete resume state detected (possible stale state), cleaning up')
        sessionStorage.removeItem(STORAGE_KEY_MANIFEST)
        sessionStorage.removeItem(STORAGE_KEY_STEP_INDEX)
        sessionStorage.removeItem('warpway:executionInProgress')
      }
    } catch (e) {
      console.error('[Warpway Executor] Failed to auto-resume', e)
      sessionStorage.removeItem('warpway:executionInProgress')
    }
  }

  /**
   * Show a notification banner when execution resumes
   */
  function showResumeNotification() {
    const banner = document.createElement('div')
    banner.id = 'warpway-resume-notification'
    banner.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(139, 92, 246, 0.3);
      z-index: 999999;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 12px;
      animation: warpway-slide-in 0.3s ease-out;
    `

    banner.innerHTML = `
      <div style="width: 20px; height: 20px; border: 2px solid white; border-top-color: transparent; border-radius: 50%; animation: warpway-spin 0.8s linear infinite;"></div>
      <span>Continuing action execution...</span>
    `

    // Add animation styles if not present
    if (!document.getElementById('warpway-resume-animations')) {
      const style = document.createElement('style')
      style.id = 'warpway-resume-animations'
      style.textContent = `
        @keyframes warpway-slide-in {
          from { transform: translateX(400px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes warpway-spin {
          to { transform: rotate(360deg); }
        }
      `
      document.head.appendChild(style)
    }

    document.body.appendChild(banner)
  }

  /**
   * Show success notification
   */
  function showSuccessNotification() {
    const banner = document.getElementById('warpway-resume-notification')
    if (banner) {
      banner.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      banner.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fill="white"/>
        </svg>
        <span>Action completed successfully!</span>
      `
      setTimeout(() => banner.remove(), 3000)
    }
  }

  /**
   * Show error notification
   */
  function showErrorNotification(message?: string) {
    const banner = document.getElementById('warpway-resume-notification')
    if (banner) {
      banner.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
      banner.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" fill="white"/>
        </svg>
        <span>Action failed: ${message || 'Unknown error'}</span>
      `
      setTimeout(() => banner.remove(), 5000)
    }
  }

  // If page has already loaded, attempt resume immediately
  if (document.readyState === 'complete') {
    // Use setTimeout to defer execution slightly so SDK finishes initializing
    setTimeout(attemptResume, 100)
  } else {
    // Otherwise, wait for load event
    window.addEventListener('load', attemptResume)
  }
}
